/**
 * 爱微圈导航 · 首次访问引导
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export const ONBOARDING_STEPS = [
    {
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
        titleKey: 'onboarding.welcome',
        descKey: 'onboarding.welcome_desc'
    },
    {
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>',
        titleKey: 'onboarding.search',
        descKey: 'onboarding.search_desc'
    },
    {
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>',
        titleKey: 'onboarding.shortcuts',
        descKey: 'onboarding.shortcuts_desc'
    },
    {
        icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
        titleKey: 'onboarding.customize',
        descKey: 'onboarding.customize_desc'
    }
];


let onbStep = 0;

export function showOnboarding() {
    const overlay = document.getElementById('onboardingOverlay');
    if (!overlay) return;
    onbStep = 0;
    renderOnbStep();
    overlay.style.display = 'flex';
    document.getElementById('onbSkip').onclick = closeOnboarding;
    document.getElementById('onbNext').onclick = nextOnbStep;
}

export function renderOnbStep() {
    const step = ONBOARDING_STEPS[onbStep];
    const body = document.getElementById('onbBody');
    const progress = document.getElementById('onbProgress');
    const nextBtn = document.getElementById('onbNext');
    const skipBtn = document.getElementById('onbSkip');
    const tt = window.i18n ? window.i18n.t.bind(window.i18n) : k => k;
    const s = state.site || {};

    // Admin-configurable onboarding text overrides (mapped by step index)
    const onbOverrides = [
        { title: s.onb_welcome_title, desc: s.onb_welcome_desc },
        { title: s.onb_search_title, desc: s.onb_search_desc },
        { title: s.onb_custom_title, desc: s.onb_custom_desc },
        { title: s.onb_done_title, desc: s.onb_done_desc },
    ];
    const ov = onbOverrides[onbStep] || {};
    const title = ov.title || tt(step.titleKey);
    const desc = ov.desc || tt(step.descKey);

    progress.innerHTML = ONBOARDING_STEPS.map((_, i) =>
        `<div class="dot${i === onbStep ? ' active' : ''}"></div>`
    ).join('');

    body.innerHTML = `
        <div class="onb-icon">${step.icon}</div>
        <h2 class="onb-title">${esc(title)}</h2>
        <p class="onb-desc">${esc(desc)}</p>
    `;

    if (onbStep === ONBOARDING_STEPS.length - 1) {
        nextBtn.textContent = tt('onboarding.done');
        skipBtn.style.display = 'none';
    } else {
        nextBtn.textContent = tt('onboarding.next');
        skipBtn.style.display = '';
        skipBtn.textContent = tt('onboarding.skip');
    }
}

export function nextOnbStep() {
    if (onbStep < ONBOARDING_STEPS.length - 1) {
        onbStep++;
        renderOnbStep();
    } else {
        closeOnboarding();
    }
}

export function closeOnboarding() {
    const overlay = document.getElementById('onboardingOverlay');
    if (overlay) overlay.style.display = 'none';
    try { localStorage.setItem('onboarding_done', '1'); } catch(e) {}
}

export function maybeShowOnboarding() {
    try {
        // 后台总开关：关闭后所有访客一律不弹（无痕模式无 localStorage 持久化，
        // 仅靠 onboarding_done 标记会在每次新会话重复弹出，故必须支持服务端关闭）
        if ((state.site || {}).onb_enabled === '0') return;
        if (!localStorage.getItem('onboarding_done')) {
            setTimeout(showOnboarding, 600);
        }
    } catch(e) {}
}

/* ==================== 浏览器通知 ==================== */

